<template>
    <el-dialog :visible="visible" :before-close="onClose" :close-on-click-modal="false" class="ah-detail-dialog network-diagnostic-dialog">
        <template slot="title"> 網路連線測速 </template>

        <div class="detail-row" style="padding-top: 20px">
            <el-alert type="info" :closable="false" show-icon class="intro-alert">
                <div>
                    這個工具會實際測試您目前所在的網路環境連線到 Sokuji 伺服器的速度，
                    藉此判斷載入速度變慢是否與您的網路環境有關。
                    測試過程約需 5~10 秒，請保持這個視窗開啟。
                </div>
            </el-alert>

            <div class="control-bar">
                <el-button type="primary" :loading="isRunning" :disabled="isRunning" @click="runDiagnostics">
                    {{ isRunning ? progressText : '開始測速' }}
                </el-button>
            </div>

            <div v-if="hasResult" class="result-area">
                <div class="conclusion-list">
                    <el-alert
                        v-for="(msg, index) in conclusion"
                        :key="index"
                        :type="msg.type"
                        :title="msg.text"
                        show-icon
                        :closable="false"
                        class="conclusion-item"
                    />
                    <el-alert
                        v-if="failedRounds > 0"
                        type="warning"
                        :title="`有 ${failedRounds} 次呼叫失敗，未列入統計，可能為當下網路中斷所致。`"
                        show-icon
                        :closable="false"
                        class="conclusion-item"
                    />
                </div>

                <div class="chart-block">
                    <div class="block-title">各階段耗時（中位數，單位：ms）</div>
                    <v-chart :option="chartOption" autoresize style="width: 100%; height: 110px" />
                </div>

                <el-table :data="detailTableData" size="small" border>
                    <el-table-column prop="label" label="階段" />
                    <el-table-column prop="value" label="耗時 (ms)" align="right" width="100" />
                </el-table>
            </div>
        </div>

        <div v-if="hasResult" class="detail-row footer" style="text-align: right">
            <el-button icon="el-icon-document-copy" size="small" @click="copyReport">複製診斷報告</el-button>
        </div>
    </el-dialog>
</template>
<script>
import { mapState, mapMutations } from 'vuex';
import { use } from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';
import { BarChart } from 'echarts/charts';
import {
    TitleComponent, TooltipComponent, LegendComponent, GridComponent,
} from 'echarts/components';
import VChart from 'vue-echarts';

use([CanvasRenderer, BarChart, TitleComponent, TooltipComponent, LegendComponent, GridComponent]);

const TEST_URL = 'Tool/SpeedTest';
const TEST_PARAMS = { sizeKB: 50 };
const ROUNDS = 5;

const BUCKETS = [
    { key: 'dns', label: 'DNS 解析' },
    { key: 'tcp', label: 'TCP 連線' },
    { key: 'tls', label: 'TLS 加密交握' },
    { key: 'stalled', label: '排隊等待' },
    { key: 'ttfb', label: '等待伺服器回應 (TTFB)' },
    { key: 'download', label: '下載內容' },
];

// 圖表視覺分組：這支測速 API 幾乎沒有業務邏輯（不查資料庫），TTFB 基本上就等於網路來回時間，
// 所以四個階段都屬於客戶端網路環境，用同一色系(藍)按連線流程先後由淺到深排列，不再需要另外標示「系統」類別
const CHART_GROUPS = [
    {
        label: '連線建立 (DNS/連線/加密)', color: '#86b6ef', keys: ['dns', 'tcp', 'tls'],
    },
    { label: '排隊等待', color: '#5598e7', keys: ['stalled'] },
    { label: '等待伺服器回應 (TTFB)', color: '#256abf', keys: ['ttfb'] },
    { label: '下載內容', color: '#184f95', keys: ['download'] },
];

function sumKeys(medianObj, keys) {
    return keys.reduce((sum, key) => sum + (medianObj[key] || 0), 0);
}

function median(arr) {
    if (!arr.length) return 0;
    const sorted = [...arr].sort((a, b) => a - b);
    const mid = Math.floor(sorted.length / 2);
    return sorted.length % 2 !== 0 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
}

function extractBuckets(entry) {
    const domainLookupEnd = entry.domainLookupEnd || entry.fetchStart;
    const connectEnd = entry.connectEnd || domainLookupEnd;

    const dns = Math.max(0, domainLookupEnd - entry.domainLookupStart);
    const tcp = Math.max(0, (entry.secureConnectionStart > 0 ? entry.secureConnectionStart : connectEnd) - entry.connectStart);
    const tls = entry.secureConnectionStart > 0 ? Math.max(0, entry.connectEnd - entry.secureConnectionStart) : 0;
    const stalled = Math.max(0, entry.requestStart - connectEnd);
    const ttfb = Math.max(0, entry.responseStart - entry.requestStart);
    const download = Math.max(0, entry.responseEnd - entry.responseStart);
    const total = Math.max(0, entry.responseEnd - entry.startTime);
    const transferSize = entry.transferSize || 0;

    return {
        dns, tcp, tls, stalled, ttfb, download, total, transferSize,
    };
}

function getConnectionInfo() {
    const conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    if (!conn) return null;
    return {
        effectiveType: conn.effectiveType,
        downlink: conn.downlink,
        rtt: conn.rtt,
    };
}

export default {
    components: {
        VChart,
    },
    data() {
        return {
            isRunning: false,
            progressText: '',
            failedRounds: 0,
            samples: [],
            resultMedian: null,
            conclusion: [],
            connectionInfo: getConnectionInfo(),
        };
    },
    computed: {
        ...mapState({
            visible: (state) => state.globalDialogState.networkDiagnosticDialogVisible,
        }),
        hasResult() {
            return this.resultMedian !== null;
        },
        detailTableData() {
            if (!this.hasResult) return [];
            const rows = BUCKETS.map((b) => ({ label: b.label, value: Math.round(this.resultMedian[b.key]) }));
            rows.push({ label: '總時間', value: Math.round(this.resultMedian.total) });
            return rows;
        },
        chartOption() {
            const data = CHART_GROUPS.map((g) => (this.resultMedian ? Math.round(sumKeys(this.resultMedian, g.keys)) : 0));

            return {
                tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
                legend: {
                    data: CHART_GROUPS.map((g) => g.label), bottom: 0, itemWidth: 12, itemHeight: 12, textStyle: { fontSize: 10 },
                },
                grid: {
                    left: 90, right: 20, top: 10, bottom: 45,
                },
                xAxis: { type: 'value', name: 'ms' },
                yAxis: { type: 'category', data: ['本次測速'] },
                series: CHART_GROUPS.map((g, index) => ({
                    name: g.label,
                    type: 'bar',
                    stack: 'total',
                    itemStyle: { color: g.color },
                    data: [data[index]],
                    barMaxWidth: 30,
                })),
            };
        },
    },
    methods: {
        ...mapMutations({
            updateVisible: 'globalDialogState/updateNetworkDiagnosticDialogVisible',
        }),
        onClose() {
            this.updateVisible(false);
        },
        async timedApiCall(callFn, urlMatcher) {
            performance.clearResourceTimings();
            await callFn();
            const entries = performance.getEntriesByType('resource').filter((e) => e.name.includes(urlMatcher));
            return entries[entries.length - 1] || null;
        },
        async runDiagnostics() {
            this.isRunning = true;
            this.samples = [];
            this.failedRounds = 0;
            this.connectionInfo = getConnectionInfo();

            for (let i = 0; i < ROUNDS; i += 1) {
                this.progressText = `測試中 (${i + 1}/${ROUNDS})`;

                try {
                    // eslint-disable-next-line no-await-in-loop
                    const entry = await this.timedApiCall(
                        () => this.$api_silent({ url: TEST_URL, method: 'GET', params: TEST_PARAMS }),
                        TEST_URL,
                    );
                    if (entry) this.samples.push(extractBuckets(entry));
                    else this.failedRounds += 1;
                } catch (ex) {
                    this.failedRounds += 1;
                }
            }

            this.computeResults();
            this.isRunning = false;
        },
        computeResults() {
            if (!this.samples.length) {
                this.resultMedian = null;
                this.$message.error('測速失敗，請稍後再試一次。');
                return;
            }

            const resultMedian = {};
            BUCKETS.concat([{ key: 'total' }]).forEach((b) => {
                resultMedian[b.key] = median(this.samples.map((s) => s[b.key]));
            });
            resultMedian.transferSize = median(this.samples.map((s) => s.transferSize));

            this.resultMedian = resultMedian;
            this.conclusion = this.buildConclusion();
        },
        buildConclusion() {
            const netSetup = this.resultMedian.dns + this.resultMedian.tcp + this.resultMedian.tls;
            const messages = [];

            if (netSetup > 150) {
                messages.push({
                    type: 'warning',
                    text: '您的網路連線建立耗時（DNS/連線/加密交握）偏高，可能與所在網路環境、VPN 或防火牆設定有關。',
                });
            }
            if (this.resultMedian.stalled > 200) {
                messages.push({
                    type: 'warning',
                    text: '請求在送出前有明顯排隊等待，可能是網路不穩定或裝置上同時有較多連線在進行。',
                });
            }

            // TTFB 判讀：這支測速 API 幾乎沒有系統處理時間（不查資料庫），TTFB 基本上就是網路來回延遲，
            // 可以直接當作網路延遲的訊號，不需要再用其他 API 相減估算
            if (this.resultMedian.ttfb > 300) {
                messages.push({
                    type: 'warning',
                    text: '偵測到網路延遲偏高（伺服器回應時間較長），可能是連線路由較遠、訊號不穩定，或使用了會增加延遲的網路環境（如部分 VPN）。',
                });
            }

            // 下載頻寬判讀：只在下載時間夠長、量測才有意義時才評估
            if (this.resultMedian.download > 5 && this.resultMedian.transferSize > 0) {
                const throughputKBps = (this.resultMedian.transferSize / this.resultMedian.download) * (1000 / 1024);
                if (throughputKBps < 300) {
                    messages.push({
                        type: 'warning',
                        text: `偵測到下載速度偏慢（推估約 ${Math.round(throughputKBps)} KB/s），可能是網路頻寬不足或訊號不穩定。`,
                    });
                }
            }

            if (!messages.length) {
                messages.push({ type: 'success', text: '您的網路連線環境測試結果正常。' });
            }
            return messages;
        },
        async copyReport() {
            const report = {
                timestamp: new Date().toISOString(),
                rounds: ROUNDS,
                failedRounds: this.failedRounds,
                connectionInfo: this.connectionInfo,
                userAgent: navigator.userAgent,
                resultMedian: this.resultMedian,
                samples: this.samples,
            };

            try {
                await navigator.clipboard.writeText(JSON.stringify(report, null, 2));
                this.$message.success('診斷報告已複製，請貼給客服人員');
            } catch (ex) {
                this.$message.error('複製失敗，請截圖畫面提供給客服人員');
            }
        },
    },
};
</script>
<style lang="scss">
.ah-detail-dialog.network-diagnostic-dialog {
    .el-dialog {
        width: 100%;
        max-width: 650px;
        margin: auto !important;
        margin-top: 5vh !important;
    }

    .intro-alert {
        margin-bottom: 15px;
    }

    .control-bar {
        display: flex;
        align-items: center;
        gap: 10px;
        margin-bottom: 8px;
    }

    .result-area {
        margin-top: 15px;

        .conclusion-list {
            display: flex;
            flex-direction: column;
            gap: 8px;
            margin-bottom: 20px;
        }

        .block-title {
            font-size: 10pt;
            font-weight: bold;
            margin-bottom: 8px;
            color: #444;
        }

        .chart-block {
            margin-bottom: 20px;
        }
    }
}
</style>
